// src/pages/builder/TopicPicturesPanel.js
//
// TOPIC PICTURES — Layout Builder panel.
//
// Makes the cartoon on every topic card (the small square beside each
// topic's title once a student or educator picks a subject) with the Image
// studio's AI, in bulk, with a look at each one before it goes live.
//
//   * Pick a subject. "Missing" lists every topic still showing the ⭐.
//   * "✨ Make pictures" makes them N at a time (start with 5 to check the
//     look). Each new picture waits on its card as "New — use it?".
//   * ✓ Use it puts it on the card for every state using the topic.
//     ↻ Try again makes another; ✕ throws it away. "✓ Use all" approves
//     every waiting picture in the subject at once.
//   * ✎ Idea lets you say exactly what to draw for a topic when the
//     automatic description (title + description + first lyric lines)
//     isn't right.
//
// Nothing reaches a student until you press ✓ — new pictures sit in
// topicLibrary/{id}.imageDraft, which no page reads.
//
// WHAT IT WRITES
// --------------
//   * topicLibrary/{id}: imageDraft, imageIdea, and on ✓ imageUrl /
//     imagePath / imagePrompt (merge — nothing else on the topic changes)
//   * topic-images/generated/… (the generateImage function) and
//     topic-images/cards/…webp (the small copy the card shows)
//   * portalLayouts + layoutBackups — ONLY via "Let the library picture
//     show", which blanks a page's own picture after backing the page up.
//
// STYLING: builder .lb-btn classes; panel rules in topicPictures.css, all
// .tp-* and scoped under `.lb-page .tp-panel`.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SUBJECTS, SUBJECT_LABELS, getAllLibraryTopics } from "../../services/topicLibraryService";
import {
  CARD_LOOKS,
  CARD_QUALITIES,
  approveTopicPicture,
  discardTopicPicture,
  findPagePictures,
  makeTopicPictureDraft,
  removeTopicPicture,
  saveTopicIdea,
  topicPicturePrompt,
  applyLibraryPictures,
} from "../../services/topicPictureService";
import "./topicPictures.css";

const FILTERS = [
  { key: "missing", label: "Missing" },
  { key: "waiting", label: "Waiting for you" },
  { key: "done", label: "Has a picture" },
  { key: "all", label: "All" },
];
const BATCH_SIZES = [5, 10, 25, 50, 0]; // 0 = all
// Two at a time: quick, and well inside the function's 5-instance limit.
const PARALLEL = 2;

const store = {
  get(k, d) {
    try {
      const v = window.localStorage.getItem(k);
      return v === null ? d : JSON.parse(v);
    } catch {
      return d;
    }
  },
  set(k, v) {
    try {
      window.localStorage.setItem(k, JSON.stringify(v));
    } catch {
      /* private window */
    }
  },
};

const idOf = (t) => t.id || t.slug;
const stateOf = (t) => (t.imageDraft?.url ? "waiting" : t.imageUrl ? "done" : "missing");

/* ------------------------------------------------------------------ */
/* One topic card                                                      */
/* ------------------------------------------------------------------ */

const PictureCard = ({ topic, busy, error, onMake, onUse, onDiscard, onRemove, onIdea }) => {
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [idea, setIdea] = useState(topic.imageIdea || "");
  const [showPrompt, setShowPrompt] = useState(false);
  useEffect(() => setIdea(topic.imageIdea || ""), [topic.imageIdea]);

  const st = stateOf(topic);
  const shown = topic.imageDraft?.url || topic.imageUrl || "";
  const live = topic.status === "published";

  return (
    <div className={`tp-card tp-${st} ${busy ? "is-busy" : ""}`}>
      <div className="tp-pic">
        {shown ? <img src={shown} alt="" loading="lazy" /> : <span className="tp-star">⭐</span>}
        {busy && (
          <div className="tp-pic-busy">
            <span className="tp-spinner" aria-hidden="true" />
            {busy === "make" ? "Making…" : busy === "use" ? "Saving…" : "…"}
          </div>
        )}
        {st === "waiting" && !busy && <span className="tp-badge">New — use it?</span>}
        {/* how it really looks on a lesson card */}
        {shown && (
          <span className="tp-mini" title="About the size students see it">
            <img src={shown} alt="" loading="lazy" />
          </span>
        )}
      </div>

      <div className="tp-info">
        <div className="tp-title" title={topic.title || topic.slug}>
          {topic.title || topic.slug}
        </div>
        <div className="tp-meta">
          <code>{topic.slug}</code>
          <span className={`tp-pill ${live ? "tp-pill-live" : "tp-pill-draft"}`} title={live ? "Live: cards show it" : "Draft topic: cards only show it once the topic is published"}>
            {live ? "live" : "draft topic"}
          </span>
        </div>
      </div>

      <div className="tp-actions">
        {st === "waiting" ? (
          <>
            <button type="button" className="lb-btn lb-btn-sm" disabled={Boolean(busy)} onClick={onUse}>
              ✓ Use it
            </button>
            <button type="button" className="lb-btn lb-btn-sm lb-btn-soft" disabled={Boolean(busy)} onClick={() => onMake(idea)}>
              ↻ Try again
            </button>
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" disabled={Boolean(busy)} onClick={onDiscard} title="Throw this one away">
              ✕
            </button>
          </>
        ) : (
          <button type="button" className={`lb-btn lb-btn-sm ${st === "done" ? "lb-btn-soft" : ""}`} disabled={Boolean(busy)} onClick={() => onMake(idea)}>
            ✨ {st === "done" ? "Make a new one" : "Make picture"}
          </button>
        )}
        <button
          type="button"
          className={`lb-btn lb-btn-sm lb-btn-ghost ${topic.imageIdea ? "tp-has-idea" : ""}`}
          onClick={() => setIdeaOpen((v) => !v)}
          title="Say exactly what to draw"
        >
          ✎ Idea{topic.imageIdea ? " •" : ""}
        </button>
        {st === "done" && (
          <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" disabled={Boolean(busy)} onClick={onRemove} title="Take the picture off the card">
            Remove
          </button>
        )}
      </div>

      {ideaOpen && (
        <div className="tp-idea">
          <textarea
            rows={3}
            value={idea}
            placeholder="e.g. a friendly analog clock with a big hour hand and long minute hand"
            onChange={(e) => setIdea(e.target.value)}
          />
          <div className="tp-idea-row">
            <button type="button" className="lb-btn lb-btn-sm" disabled={Boolean(busy)} onClick={() => onMake(idea)}>
              ✨ Make with this idea
            </button>
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={() => onIdea(idea)}>
              Save idea
            </button>
            <button type="button" className="tp-link" onClick={() => setShowPrompt((v) => !v)}>
              {showPrompt ? "Hide" : "Show"} what gets sent
            </button>
          </div>
          {showPrompt && <pre className="tp-prompt">{topicPicturePrompt(topic, idea)}</pre>}
        </div>
      )}

      {error && <p className="tp-error">{error}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Pages that carry their own picture                                  */
/* ------------------------------------------------------------------ */

const PageOverrides = ({ topics }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const libBySlug = useMemo(() => {
    const m = {};
    topics.forEach((t) => {
      m[String(t.slug || t.id).toLowerCase()] = t;
      m[String(t.id || t.slug).toLowerCase()] = t;
    });
    return m;
  }, [topics]);

  const scan = async () => {
    setBusy("scan");
    setMsg(null);
    try {
      setRows(await findPagePictures());
    } catch (err) {
      setMsg({ tone: "bad", text: `Couldn’t read the pages: ${err?.message || err}` });
    } finally {
      setBusy("");
    }
  };

  // only offer to clear where the library actually has a picture to show
  const fixable = (rows || []).filter((r) => libBySlug[r.slug]?.imageUrl);

  const fix = async (slugs) => {
    const pages = (rows || []).filter((r) => slugs.includes(r.slug)).reduce((n, r) => n + r.pages.length, 0);
    if (
      !window.confirm(
        `Let the library picture show on ${pages} page${pages === 1 ? "" : "s"}?\n\nThis clears each page's own picture for ${
          slugs.length === 1 ? "that topic" : `those ${slugs.length} topics`
        }. Every page is backed up to layoutBackups first.`
      )
    )
      return;
    setBusy("fix");
    setMsg({ tone: "info", text: "Updating pages…" });
    try {
      const out = await applyLibraryPictures(slugs, (done, total) => setMsg({ tone: "info", text: `Updating pages… ${done} of ${total}` }));
      setMsg({ tone: "ok", text: `Done: ${out.topics} topic card${out.topics === 1 ? "" : "s"} on ${out.pages} page${out.pages === 1 ? "" : "s"} now show the library picture.` });
      setRows(await findPagePictures());
    } catch (err) {
      setMsg({ tone: "bad", text: `Stopped: ${err?.message || err}. Pages already done are fine; their backups are in layoutBackups.` });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="tp-overrides">
      <div className="tp-overrides-head">
        <div>
          <strong>Pages with their own picture</strong>
          <p className="tp-tiny">
            A page that saved its own picture for a topic keeps it — the library picture only fills a blank. Check
            here once your pictures are in.
          </p>
        </div>
        <button type="button" className="lb-btn lb-btn-sm lb-btn-soft" onClick={scan} disabled={Boolean(busy)}>
          {busy === "scan" ? "Checking every page…" : rows ? "Check again" : "Check pages"}
        </button>
      </div>
      {msg && <div className={`tp-msg tp-msg-${msg.tone}`}>{msg.text}</div>}
      {rows &&
        (rows.length === 0 ? (
          <p className="tp-tiny">No page has its own picture — every card shows the library picture.</p>
        ) : (
          <>
            <p className="tp-tiny">
              {rows.length} topic{rows.length === 1 ? " has its" : "s have their"} own picture on some pages.{" "}
              {fixable.length > 0 && (
                <button type="button" className="lb-btn lb-btn-sm" disabled={Boolean(busy)} onClick={() => fix(fixable.map((r) => r.slug))}>
                  Let the library picture show for all {fixable.length}
                </button>
              )}
            </p>
            <ul className="tp-over-list">
              {rows.map((r) => {
                const lib = libBySlug[r.slug];
                const own = Array.from(r.urls)[0];
                return (
                  <li key={r.slug}>
                    <span className="tp-over-pics">
                      <img src={own} alt="" title="The page's own picture" />
                      <span>→</span>
                      {lib?.imageUrl ? <img src={lib.imageUrl} alt="" title="The library picture" /> : <span className="tp-star tp-star-sm">⭐</span>}
                    </span>
                    <span className="tp-over-name">
                      <strong>{lib?.title || r.slug}</strong> <code>{r.slug}</code>
                      <span className="tp-tiny">
                        {" "}
                        · on {r.pages.length} page{r.pages.length === 1 ? "" : "s"}
                      </span>
                    </span>
                    {lib?.imageUrl ? (
                      <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" disabled={Boolean(busy)} onClick={() => fix([r.slug])}>
                        Use library picture
                      </button>
                    ) : (
                      <span className="tp-tiny">no library picture yet</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const TopicPicturesPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [subject, setSubject] = useState("science");
  const [filter, setFilter] = useState("missing");
  const [search, setSearch] = useState("");
  const [onlyLive, setOnlyLive] = useState(false);
  const [look, setLook] = useState(() => store.get("sbl.cardPics.look", "sbl"));
  const [quality, setQuality] = useState(() => store.get("sbl.cardPics.quality", "standard"));
  const [batchSize, setBatchSize] = useState(5);
  const [busy, setBusy] = useState({}); // id -> "make" | "use"
  const [errors, setErrors] = useState({}); // id -> message
  const [run, setRun] = useState(null); // { done, total, failed, stopping }
  const [notice, setNotice] = useState(null);
  const stopRef = useRef(false);

  useEffect(() => store.set("sbl.cardPics.look", look), [look]);
  useEffect(() => store.set("sbl.cardPics.quality", quality), [quality]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTopics(await getAllLibraryTopics());
    } catch (err) {
      setNotice({ tone: "bad", text: `Couldn’t load the topics: ${err?.message || err}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // keep in step with saves made elsewhere (and our own)
  useEffect(() => {
    const on = (e) => {
      const { id, patch } = e.detail || {};
      if (!id) return;
      setTopics((prev) => prev.map((t) => (idOf(t) === id ? { ...t, ...patch } : t)));
    };
    window.addEventListener("sbl:topic-updated", on);
    return () => window.removeEventListener("sbl:topic-updated", on);
  }, []);

  // stop a running batch if the panel goes away
  useEffect(() => () => {
    stopRef.current = true;
  }, []);

  const inSubject = useMemo(() => topics.filter((t) => (t.subject || "other") === subject), [topics, subject]);

  const counts = useMemo(() => {
    const c = {};
    SUBJECTS.forEach((s) => {
      const rows = topics.filter((t) => (t.subject || "other") === s);
      c[s] = {
        total: rows.length,
        missing: rows.filter((t) => stateOf(t) === "missing").length,
        waiting: rows.filter((t) => stateOf(t) === "waiting").length,
        done: rows.filter((t) => stateOf(t) === "done").length,
      };
    });
    return c;
  }, [topics]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inSubject
      .filter((t) => filter === "all" || stateOf(t) === filter)
      .filter((t) => !onlyLive || t.status === "published")
      .filter((t) => !q || `${t.title} ${t.slug}`.toLowerCase().includes(q));
  }, [inSubject, filter, onlyLive, search]);

  const setBusyFor = (id, v) =>
    setBusy((b) => {
      const n = { ...b };
      if (v) n[id] = v;
      else delete n[id];
      return n;
    });
  const setErrorFor = (id, v) =>
    setErrors((e) => {
      const n = { ...e };
      if (v) n[id] = v;
      else delete n[id];
      return n;
    });

  const makeOne = async (topic, idea = topic.imageIdea || "") => {
    const id = idOf(topic);
    setBusyFor(id, "make");
    setErrorFor(id, "");
    try {
      await makeTopicPictureDraft(topic, { idea, look, quality });
      return true;
    } catch (err) {
      setErrorFor(id, err?.message || String(err));
      return false;
    } finally {
      setBusyFor(id, "");
    }
  };

  const approveOne = async (topic) => {
    const id = idOf(topic);
    setBusyFor(id, "use");
    setErrorFor(id, "");
    try {
      const out = await approveTopicPicture(topic);
      if (!out.small) {
        setNotice({
          tone: "info",
          text: "Used the full-size picture: this browser wasn’t allowed to shrink it (the bucket’s CORS must list this site). It still shows; it just loads a little slower.",
        });
      }
      return true;
    } catch (err) {
      setErrorFor(id, err?.message || String(err));
      return false;
    } finally {
      setBusyFor(id, "");
    }
  };

  /** Make pictures for the topics in this subject still missing one. */
  const runBatch = async () => {
    const queue = inSubject
      .filter((t) => stateOf(t) === "missing")
      .filter((t) => !onlyLive || t.status === "published")
      .sort((a, b) => String(a.title || a.slug).localeCompare(String(b.title || b.slug)));
    const list = batchSize ? queue.slice(0, batchSize) : queue;
    if (!list.length) return;
    if (
      list.length > 10 &&
      !window.confirm(
        `Make ${list.length} pictures? Each one is a small charge on the Gemini account${quality === "best" ? " (Best detail costs the most)" : ""}. Keep this tab open while it runs — you can stop any time and keep what's made.`
      )
    )
      return;
    stopRef.current = false;
    setFilter((f) => (f === "done" ? "missing" : f));
    let done = 0;
    let failed = 0;
    setRun({ done: 0, total: list.length, failed: 0 });
    let next = 0;
    const worker = async () => {
      while (!stopRef.current && next < list.length) {
        const topic = list[next];
        next += 1;
        // eslint-disable-next-line no-await-in-loop
        const ok = await makeOne(topic);
        done += 1;
        if (!ok) failed += 1;
        setRun({ done, total: list.length, failed });
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, list.length) }, worker));
    setRun(null);
    setNotice({
      tone: failed ? "bad" : "ok",
      text: stopRef.current
        ? `Stopped after ${done}. Everything made so far is waiting for you below.`
        : `Made ${done - failed} picture${done - failed === 1 ? "" : "s"}${failed ? `; ${failed} failed (see the red notes)` : ""}. Look them over and press ✓ Use it — or ✓ Use all.`,
    });
    if (!stopRef.current) setFilter("waiting");
  };

  const approveAll = async () => {
    const list = inSubject.filter((t) => stateOf(t) === "waiting");
    if (!list.length) return;
    if (!window.confirm(`Put all ${list.length} new pictures on their topic cards?`)) return;
    stopRef.current = false;
    let done = 0;
    setRun({ done: 0, total: list.length, failed: 0, approving: true });
    for (const t of list) {
      if (stopRef.current) break;
      // eslint-disable-next-line no-await-in-loop
      await approveOne(t);
      done += 1;
      setRun({ done, total: list.length, failed: 0, approving: true });
    }
    setRun(null);
    setNotice({ tone: "ok", text: `${done} picture${done === 1 ? " is" : "s are"} now on the topic cards.` });
  };

  const c = counts[subject] || { total: 0, missing: 0, waiting: 0, done: 0 };
  const missingToMake = inSubject.filter((t) => stateOf(t) === "missing" && (!onlyLive || t.status === "published")).length;
  const willMake = batchSize ? Math.min(batchSize, missingToMake) : missingToMake;

  return (
    <section className="lb-card tp-panel">
      <div className="lb-card-head">
        <span className="lb-step tp-step">🖼</span>
        <div>
          <h2>Topic pictures</h2>
          <p className="lb-hint">
            The cartoon on each topic card students and educators see after picking a subject. Make them with AI in
            bulk, check each one, then put it on the card — once per topic, for every state.
          </p>
        </div>
        <div className="tp-head-actions">
          {open && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          <div className="tp-tabs">
            {SUBJECTS.filter((s) => (counts[s]?.total || 0) > 0 || s === subject).map((s) => (
              <button key={s} type="button" className={`tp-tab ${subject === s ? "is-on" : ""}`} onClick={() => setSubject(s)}>
                {SUBJECT_LABELS[s]}
                <span className="tp-tab-n">
                  {counts[s]?.done || 0}/{counts[s]?.total || 0}
                </span>
                {counts[s]?.waiting > 0 && <span className="tp-tab-wait">{counts[s].waiting}</span>}
              </button>
            ))}
          </div>

          {/* ---- make in bulk ---- */}
          <div className="tp-batch">
            <div className="tp-batch-row">
              <label>
                Look
                <select value={look} onChange={(e) => setLook(e.target.value)} disabled={Boolean(run)}>
                  {CARD_LOOKS.map((l) => (
                    <option key={l.key} value={l.key}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Quality
                <select value={quality} onChange={(e) => setQuality(e.target.value)} disabled={Boolean(run)}>
                  {CARD_QUALITIES.map((q) => (
                    <option key={q.key} value={q.key}>
                      {q.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                How many
                <select value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value))} disabled={Boolean(run)}>
                  {BATCH_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n ? `${n}${n === 5 ? " (test the look)" : ""}` : `All missing (${missingToMake})`}
                    </option>
                  ))}
                </select>
              </label>
              {run ? (
                <button type="button" className="lb-btn lb-btn-danger" onClick={() => (stopRef.current = true)}>
                  ■ Stop
                </button>
              ) : (
                <button type="button" className="lb-btn" onClick={runBatch} disabled={!willMake}>
                  ✨ Make {willMake} picture{willMake === 1 ? "" : "s"}
                </button>
              )}
              {c.waiting > 0 && !run && (
                <button type="button" className="lb-btn lb-btn-soft" onClick={approveAll}>
                  ✓ Use all {c.waiting} waiting
                </button>
              )}
            </div>
            <p className="tp-tiny">
              {SUBJECT_LABELS[subject]}: <b>{c.done}</b> with a picture · <b>{c.waiting}</b> waiting for you ·{" "}
              <b>{c.missing}</b> still showing ⭐. New pictures wait for your ✓ — nothing changes for students until
              then.
            </p>
            {run && (
              <div className="tp-progress">
                <div className="tp-progress-bar" style={{ width: `${Math.round((run.done / run.total) * 100)}%` }} />
                <span>
                  {run.approving ? "Saving" : "Making"} {run.done} of {run.total}
                  {run.failed ? ` · ${run.failed} failed` : ""}
                  {run.approving ? "" : " — about 15 seconds each, two at a time. Keep this tab open."}
                </span>
              </div>
            )}
          </div>

          {notice && (
            <div className={`tp-msg tp-msg-${notice.tone}`}>
              {notice.text}
              <button type="button" className="tp-x" onClick={() => setNotice(null)} aria-label="Dismiss">
                ✕
              </button>
            </div>
          )}

          <div className="tp-controls">
            <div className="tp-seg" role="group" aria-label="Show">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" className={filter === f.key ? "is-on" : ""} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label}
                  <span>{f.key === "all" ? c.total : c[f.key]}</span>
                </button>
              ))}
            </div>
            <input className="tp-search" placeholder="Search topics…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <label className="tp-check">
              <input type="checkbox" checked={onlyLive} onChange={(e) => setOnlyLive(e.target.checked)} />
              Live topics only
            </label>
          </div>

          {loading && !topics.length ? (
            <p className="tp-tiny">Loading topics…</p>
          ) : visible.length === 0 ? (
            <p className="tp-empty">
              {filter === "missing"
                ? `Every ${SUBJECT_LABELS[subject]} topic has a picture or one waiting. 🎉`
                : filter === "waiting"
                ? "Nothing waiting. Make some pictures above."
                : "No topics match."}
            </p>
          ) : (
            <div className="tp-grid">
              {visible.map((t) => (
                <PictureCard
                  key={idOf(t)}
                  topic={t}
                  busy={busy[idOf(t)]}
                  error={errors[idOf(t)]}
                  onMake={(idea) => makeOne(t, idea)}
                  onUse={() => approveOne(t)}
                  onDiscard={async () => {
                    try {
                      await discardTopicPicture(t);
                    } catch (err) {
                      setErrorFor(idOf(t), err?.message || String(err));
                    }
                  }}
                  onRemove={async () => {
                    if (!window.confirm(`Take the picture off "${t.title || t.slug}"? Its card goes back to the ⭐.`)) return;
                    try {
                      await removeTopicPicture(t);
                    } catch (err) {
                      setErrorFor(idOf(t), err?.message || String(err));
                    }
                  }}
                  onIdea={async (idea) => {
                    try {
                      await saveTopicIdea(t, idea);
                      setNotice({ tone: "ok", text: `Idea saved for “${t.title || t.slug}”. Batches use it too.` });
                    } catch (err) {
                      setErrorFor(idOf(t), err?.message || String(err));
                    }
                  }}
                />
              ))}
            </div>
          )}

          <PageOverrides topics={topics} />
        </>
      )}
    </section>
  );
};

export default TopicPicturesPanel;
